import React, { useState } from "react";

import Checkbox from "@material-ui/core/Checkbox";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  TextInput,
  InputAdornment,
  Accordion,
  AccordionSummary,
  SelectInput,
  MenuItem,
  Collapse,
} from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import { FreightClass, PackagingType } from "api/types/generated-types";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { freightClassDictionary } from "app/pages/shipments/components/EditableShipmentPackageGroupElements";
import clsx from "clsx";
import { Guide } from "components/Guide";
import Text from "components/Text";
import FreightClassCalculator from "features/shipments/FreightClassCalculator";
import filter from "lodash/filter";
import uniqueId from "lodash/uniqueId";
import {
  ExtraPalletsType,
  packagingTypeList,
  PackagingTypeListItem,
} from "pages/shipper/pages/request-quote/pages/ltl/steps/shipmentDetails/constants/packagingTypes";
import { Controller, useFormContext, useFieldArray, FieldError, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isStackable } from "utils/ltl/isStackable";

import { FinalSchema } from "../../_schemas";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInitialPackageGroup = (): any => ({
  id: uniqueId("temp-"),
  item_type: "",
  item_quantity: "",
  is_stackable: true,
  length_per_package: "",
  width_per_package: "",
  height_per_package: "",
  weight_per_package: "",
  weight_type: "per",
  freight_class: "",
  nmfc_code: "",
  commodity: "",
});

export const CargoSection: React.FC = () => {
  const { t } = useTranslation("quoteRequest");
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FinalSchema>();
  const { quoteRequest: airQuoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setQuoteRequestByPath = quoteRequestActions.useSetQuoteRequestByPath();

  const { fields } = useFieldArray<FinalSchema>({
    control,
    rules: {
      minLength: 1,
    },
    name: "ltl_load_spec.package_groups",
  });

  const [expanded, setExpanded] = useState<number | false>(fields.length > 0 ? 0 : false);
  const [mapIdToWeightType, setMapIdToWeightType] = useState<Record<string, "per" | "total">>({});

  const handleAccordionChange = (idx: number) => {
    if (idx === expanded) {
      setExpanded(false);
    } else {
      setExpanded(idx);
    }
  };

  const baseError = errors.ltl_load_spec as Record<string, FieldError | FieldErrors> | undefined;
  const groupErrors = (baseError?.package_groups as FieldErrors[] | undefined) || [];

  return (
    <section className="mb-6 max-w-[70%]">
      <h3 className="text-md text-gray-500 font-semibold mb-2">{t("CargoStep.CargoSection.title")}</h3>
      <div className="flex flex-col rounded-md">
        {fields.map((_field, index) => {
          const packageGroup = watch(`ltl_load_spec.package_groups.${index}`);
          const itemQuantity = watch(`ltl_load_spec.package_groups.${index}.item_quantity`);
          const itemType = watch(`ltl_load_spec.package_groups.${index}.packaging_type`);
          const length = watch(`ltl_load_spec.package_groups.${index}.length_per_package`);
          const width = watch(`ltl_load_spec.package_groups.${index}.width_per_package`);
          const height = watch(`ltl_load_spec.package_groups.${index}.height_per_package`);

          const dimensionErrors =
            errors.ltl_load_spec?.package_groups?.[index]?.length_per_package?.message ??
            errors.ltl_load_spec?.package_groups?.[index]?.height_per_package?.message ??
            errors.ltl_load_spec?.package_groups?.[index]?.width_per_package?.message;

          // @ts-expect-error we set the id locally but the typing is wrong
          const weightType = mapIdToWeightType[packageGroup.id] || "per";
          const weight = watch(`ltl_load_spec.package_groups.${index}.weight_per_package`);

          const freightClassDefaults = {
            length,
            width,
            height,
            weight,
          };

          const itemTypeLabel = itemType ? t(`packagingTypes.${itemType as PackagingTypeListItem}`) : "";

          const hasGroupError = !!groupErrors[index] && Object.keys(groupErrors[index] || {}).length > 0;

          return (
            <div
              className={clsx(
                "w-full border",
                hasGroupError ? "border-red-500" : "border-gray-200",
                index === 0 ? "rounded-t-xl" : ""
              )}
              // @ts-expect-error we set the id locally but the typing is wrong
              key={packageGroup.id}
            >
              <div
                className="flex flex-row items-center justify-between px-5 py-4 border-b"
                onClick={() => handleAccordionChange(index)}
              >
                <span className="font-semibold text-base">
                  {t("CargoStep.CargoSection.itemLabel", { index: index + 1 })}
                </span>
                <div className="flex flex-row">
                  {itemQuantity && itemTypeLabel && length && width && height && weight ? (
                    <span className="inline-flex items-center gap-1 bg-[#EAEEFD] rounded-lg px-3 py-1 ml-2">
                      <span className="font-bold text-blue-600">
                        {itemQuantity} x {itemTypeLabel}
                      </span>
                      <span className="text-blue-600">
                        {length} x {width} x {height} in, {weight}
                        lb
                      </span>
                    </span>
                  ) : null}
                  <ExpandMoreIcon
                    className="transition-transform duration-200"
                    style={{ transform: expanded === index ? "rotate(180deg)" : "rotate(0deg)" }}
                  />
                </div>
              </div>
              <Collapse in={expanded === index}>
                <div className="flex flex-col gap-4 px-5 py-4">
                  <div className="flex flex-col">
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-[220px]">
                        <label className="block text-sm font-bold mb-1">
                          {t("CargoStep.CargoSection.itemTypeLabel")}
                          <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.packaging_type`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <Guide showGuide={!field.value && !fieldState.error}>
                              <SelectInput
                                value={field.value}
                                onChange={(event) => {
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.packaging_type`,
                                    value: event.target.value,
                                  });

                                  if (event.target.value === ExtraPalletsType.Pallets_48_48) {
                                    setQuoteRequestByPath({
                                      path: `load_spec.package_groups.${index}.length_per_package`,
                                      value: 48,
                                    });
                                    setQuoteRequestByPath({
                                      path: `load_spec.package_groups.${index}.width_per_package`,
                                      value: 48,
                                    });
                                  } else if (event.target.value === ExtraPalletsType.Pallets_48_40) {
                                    setQuoteRequestByPath({
                                      path: `load_spec.package_groups.${index}.length_per_package`,
                                      value: 48,
                                    });
                                    setQuoteRequestByPath({
                                      path: `load_spec.package_groups.${index}.width_per_package`,
                                      value: 40,
                                    });
                                  }
                                }}
                                error={!!fieldState.error}
                                fullWidth
                              >
                                {packagingTypeList.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {t(`CargoStep.packagingTypes.${type}`, { defaultValue: type })}
                                  </MenuItem>
                                ))}
                              </SelectInput>
                              {fieldState.error && (
                                <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                  {fieldState.error.message}
                                </p>
                              )}
                            </Guide>
                          )}
                        />
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-sm font-bold mb-1">
                          {t("CargoStep.CargoSection.totalLabel")}
                          <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.item_quantity`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <Guide showGuide={!field.value && !fieldState.error}>
                              <TextInput
                                type="number"
                                value={field.value}
                                onChange={(e) =>
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.item_quantity`,
                                    value: Number(e.target.value),
                                  })
                                }
                                inputProps={{ min: 1 }}
                                error={!!fieldState.error}
                                fullWidth
                              />
                              {fieldState.error && (
                                <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                  {fieldState.error.message}
                                </p>
                              )}
                            </Guide>
                          )}
                        />
                      </div>
                    </div>
                    {/* Stackable Checkbox */}
                    <div className="flex">
                      {isStackable(itemType as PackagingType) && (
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.is_stackable`}
                          control={control}
                          render={({ field }) => (
                            <button
                              type="button"
                              className="flex items-center focus:outline-none"
                              onClick={() => field.onChange(!field.value)}
                              aria-pressed={field.value}
                            >
                              <Checkbox
                                checked={!!field.value}
                                onChange={() =>
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.is_stackable`,
                                    value: !field.value,
                                  })
                                }
                              />
                              <span className="text-base font-normal text-gray-900 select-none">
                                {t(`CargoStep.stackableItemTypeLabel`, {
                                  itemType: itemTypeLabel,
                                })}
                              </span>
                            </button>
                          )}
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <Text
                      size="small"
                      weight="bold"
                      required
                      typographyProps={{ className: "block text-sm font-bold", style: { marginBottom: "4px" } }}
                    >
                      {t("CargoStep.CargoSection.dimensionsLabel")}
                    </Text>
                    <Guide showGuide={!length && !width && !height && !dimensionErrors}>
                      <div
                        className={`flex items-center content-center gap-6 max-w-fit text-center ${
                          !!dimensionErrors ? "border-2 border-red-500 rounded-md p-2" : ""
                        }`}
                      >
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.length_per_package`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col min-w-[70px]">
                              <TextInput
                                type="number"
                                value={field.value}
                                onChange={(e) =>
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.length_per_package`,
                                    value: Number(e.target.value),
                                  })
                                }
                                placeholder={t("CargoStep.CargoSection.dimensionPlaceholders.length")}
                                error={!!fieldState.error}
                              />
                            </div>
                          )}
                        />
                        <span className="text-3xl">×</span>
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.width_per_package`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col min-w-[70px]">
                              <TextInput
                                type="number"
                                value={field.value}
                                onChange={(e) =>
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.width_per_package`,
                                    value: Number(e.target.value),
                                  })
                                }
                                placeholder={t("CargoStep.CargoSection.dimensionPlaceholders.width")}
                                error={!!fieldState.error}
                              />
                            </div>
                          )}
                        />
                        <span className="text-3xl">×</span>
                        <Controller
                          name={`ltl_load_spec.package_groups.${index}.height_per_package`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col min-w-[70px]">
                              <TextInput
                                type="number"
                                value={field.value}
                                onChange={(e) =>
                                  setQuoteRequestByPath({
                                    path: `load_spec.package_groups.${index}.height_per_package`,
                                    value: Number(e.target.value),
                                  })
                                }
                                placeholder={t("CargoStep.CargoSection.dimensionPlaceholders.height")}
                                error={!!fieldState.error}
                              />
                            </div>
                          )}
                        />
                      </div>
                      {dimensionErrors && (
                        <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                          {t("CargoStep.CargoSection.dimensionsInvalid")}
                        </p>
                      )}
                    </Guide>
                  </div>
                  <div className="flex flex-wrap gap-2 items-start">
                    <div className="flex-1">
                      <label className="block text-sm font-bold mb-1 ">
                        {t("CargoStep.CargoSection.weightTypeLabel")}
                      </label>
                      <div className="flex mx-2">
                        <button
                          type="button"
                          className={`px-4 py-2 border rounded flex-1 transition-colors ${
                            weightType === "total"
                              ? "text-blue-600 font-bold outline outline-2 outline-blue-500 z-10"
                              : "bg-[#F3F4F5]"
                          }`}
                          onClick={() => {
                            // @ts-expect-error we set the id locally but the typing is wrong
                            setMapIdToWeightType({ ...mapIdToWeightType, [packageGroup.id]: "total" });

                            setValue(`ltl_load_spec.package_groups.${index}.weight_type`, "total");
                          }}
                        >
                          {t("CargoStep.CargoSection.weightTypeTotal")}
                        </button>
                        <button
                          type="button"
                          className={`px-4 py-2 border rounded flex-1 transition-colors ${
                            weightType === "per"
                              ? "text-blue-600 font-bold outline outline-2 outline-blue-500 z-10"
                              : "bg-[#F3F4F5]"
                          }`}
                          onClick={() => {
                            // @ts-expect-error we set the id locally but the typing is wrong
                            setMapIdToWeightType({ ...mapIdToWeightType, [packageGroup.id]: "per" });

                            setValue(`ltl_load_spec.package_groups.${index}.weight_type`, "per");
                          }}
                        >
                          {t("CargoStep.CargoSection.weightTypePer")}
                        </button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-bold mb-1 after:content-['*'] after:text-red-500">
                        {weightType === "per"
                          ? t("CargoStep.CargoSection.weightPerItemLabel")
                          : t("CargoStep.CargoSection.totalWeightLabel")}
                      </label>
                      <Controller
                        name={`ltl_load_spec.package_groups.${index}.weight_per_package`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <Guide showGuide={!field.value && !fieldState.error}>
                            <TextInput
                              type="number"
                              fullWidth
                              value={weightType === "per" ? field.value : field.value * (itemQuantity || 1)}
                              onChange={(e) =>
                                setQuoteRequestByPath({
                                  path: `load_spec.package_groups.${index}.weight_per_package`,
                                  value:
                                    weightType === "per"
                                      ? Number(e.target.value)
                                      : Number(e.target.value) / (itemQuantity || 1),
                                })
                              }
                              inputProps={{ min: 0 }}
                              error={!!fieldState.error}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">lb</InputAdornment>,
                              }}
                            />
                            {fieldState.error && (
                              <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                {fieldState.error.message}
                              </p>
                            )}
                          </Guide>
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 w-full">
                    <div className="flex flex-col flex-1">
                      <label className="block text-sm font-bold mb-1">
                        {t("CargoStep.CargoSection.freightClassLabel")}
                      </label>
                      <Controller
                        name={`ltl_load_spec.package_groups.${index}.freight_class`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <SelectInput
                              value={field.value}
                              onChange={(e) =>
                                setQuoteRequestByPath({
                                  path: `load_spec.package_groups.${index}.freight_class`,
                                  value: e.target.value,
                                })
                              }
                              error={!!fieldState.error}
                              fullWidth
                            >
                              {Object.values(FreightClass).map((fc) => (
                                <MenuItem key={fc} value={fc}>
                                  {freightClassDictionary[fc]}
                                </MenuItem>
                              ))}
                            </SelectInput>
                            {fieldState.error && (
                              <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                {fieldState.error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                      <div className="pt-2">
                        <FreightClassCalculator
                          defaultValues={freightClassDefaults}
                          onConfirm={(value) =>
                            setQuoteRequestByPath({
                              path: `load_spec.package_groups.${index}.freight_class`,
                              value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex flex-col flex-1">
                      <label className="block text-sm font-bold mb-1">
                        {t("CargoStep.CargoSection.nmfcCodeLabel")}
                      </label>
                      <Controller
                        name={`ltl_load_spec.package_groups.${index}.nmfc_code`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <TextInput
                              type="text"
                              fullWidth
                              value={field.value}
                              onChange={(e) =>
                                setQuoteRequestByPath({
                                  path: `load_spec.package_groups.${index}.nmfc_code`,
                                  value: e.target.value,
                                })
                              }
                              error={!!fieldState.error}
                            />
                            {fieldState.error && (
                              <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                {fieldState.error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                  <div>
                    <div>
                      <label className="block text-sm font-bold mb-1">
                        {t("CargoStep.CargoSection.commodityLabel")}
                      </label>

                      <Controller
                        name={`ltl_load_spec.package_groups.${index}.commodities`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <FormStringConcat
                              value={field.value ?? ""}
                              onChange={(value) =>
                                setQuoteRequestByPath({
                                  path: `load_spec.package_groups.${index}.commodities`,
                                  value,
                                })
                              }
                              placeholder={t("CargoStep.CargoSection.commodityPlaceholder")}
                              style={{ marginRight: "12px" }}
                            />
                            {fieldState.error && (
                              <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">
                                {fieldState.error.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      className="text-red-500 border border-red-500 rounded px-3 py-1 hover:bg-red-50 transition-colors"
                      style={{ outline: "1px solid #ef4444" }}
                      onClick={() => {
                        setQuoteRequestAction({
                          load_spec: {
                            package_groups: filter(
                              airQuoteRequest.load_spec?.package_groups,
                              (_, index) => index !== index
                            ),
                          },
                        });
                        setExpanded(index === 0 ? false : index - 1);
                      }}
                    >
                      {t("CargoStep.CargoSection.removeButton")}
                    </button>
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })}
        <Accordion
          onClick={() => {
            setQuoteRequestAction({
              load_spec: {
                package_groups: [...(airQuoteRequest.load_spec?.package_groups ?? []), getInitialPackageGroup()],
              },
            });
            setExpanded(fields.length);
          }}
          className={clsx("border border-gray-200 rounded-b-xl", fields.length === 0 ? "rounded-t-xl" : "")}
        >
          <AccordionSummary>
            <div className="flex items-center gap-4 px-2 focus:outline-none" data-testid="add-another-item">
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#0045FF]">
                <AddCircleIcon style={{ fontSize: 40, color: "#EAEEFD" }} />
              </span>
              <span className="font-bold text-md text-gray-900">{t("CargoStep.CargoSection.addAnotherItem")}</span>
            </div>
          </AccordionSummary>
        </Accordion>
      </div>

      {baseError?.package_groups && (
        <p className="text-red-500 text-xs font-bold pt-1 tracking-[1.2px]">{baseError?.package_groups.message}</p>
      )}
    </section>
  );
};
